<template>
    <nav class="navbar is-flex">
        <div class="navbar-brand">
            <a
                class="navbar-item navbar-logo"
                :href="
                    `/${
                        this.$i18n.locale.split('-')[0] !== 'de'
                            ? this.$i18n.locale.split('-')[0]
                            : ''
                    }`
                "
                @click.prevent="resetOrderProcess()"
                :aria-label="this.$i18n.t('navigation.aria.mainpage')"
            >
                <img
                    alt="Murtfeldt Additive Solutions Logo"
                    src="@/assets/img/logo.png"
                />
            </a>
            <template
                v-if="
                    [viewports.MOBILE.name, viewports.TABLET.name].includes(
                        currentViewport
                    )
                "
            >
                <div
                    class="navbar-item navbar-upload"
                    :class="{ 'is-invisible': !showUploadButton }"
                    @click="showFileUpload"
                    :aria-label="this.$i18n.t('navigation.aria.uploadButton')"
                >
                    <div class="icon-container">
                        <img
                            alt="Upload Icon"
                            src="@/assets/img/lnr-upload.svg"
                        />
                    </div>
                    {{ $t('navigation.labels.uploadFile') }}
                </div>
                <a
                    role="button"
                    class="navbar-burger"
                    @click="toggleMenu"
                    :class="{ 'is-active': menuIsExpanded }"
                >
                    <span aria-hidden="true"></span>
                    <span aria-hidden="true"></span>
                    <span aria-hidden="true"></span>
                </a>
            </template>
        </div>
        <div
            class="navbar-menu"
            :class="{ 'is-active': menuIsExpanded }"
            v-if="mainNavigation"
            @click="menuIsExpanded = false"
        >
            <div class="navbar-start">
                <RouterLink
                    v-for="(navigationLink, index) in mainNavigation"
                    :key="'navigation_main_' + index"
                    :to="navigationLink.link"
                    class="navbar-item navbar-item-hover"
                >
                    {{ navigationLink.title }}
                </RouterLink>
                <template
                    v-if="
                        [
                            viewports.DESKTOP.name,
                            viewports.WIDESCREEN.name,
                            viewports.FULL_HD.name,
                        ].includes(currentViewport)
                    "
                >
                    <div class="navbar-spacer"></div>
                    <RouterLink
                        :to="isUserLoggedIn ? '/account' : '/login'"
                        class="navbar-item navbar-item-hover with-icon"
                    >
                        <SvgIcon
                            icon="icon--web-application_user"
                            class="is-dark"
                            :grow-by-height="true"
                        />

                        {{
                            isUserLoggedIn
                                ? $t('navigation.labels.account')
                                : $t('login.mainActions.login')
                        }}
                    </RouterLink>
                    <a
                        v-if="isUserLoggedIn"
                        @click="logout"
                        class="navbar-item navbar-item-hover"
                    >
                        {{ $t('login.mainActions.logout') }}
                    </a>
                </template>
            </div>
            <div
                class="navbar-end"
                v-if="
                    [viewports.MOBILE.name, viewports.TABLET.name].includes(
                        currentViewport
                    )
                "
            >
                <div
                    class="navbar-section"
                    v-if="Object.keys(allCartItems).length > 0"
                >
                    <div class="navbar-section-title">
                        {{ this.$i18n.t('checkout.cart.label') }}
                    </div>

                    <RouterLink
                        to="/cart"
                        class="navbar-item navbar-item-hover with-icon"
                    >
                        <SvgIcon
                            icon="icon--web-application_cart"
                            class="is-dark"
                            :grow-by-height="true"
                        />
                        {{ `${Object.keys(allCartItems).length} ` }}
                        {{
                            Object.keys(allCartItems).length > 1
                                ? this.$i18n.t(
                                      'checkout.cart.itemLabels.namePlural'
                                  )
                                : this.$i18n.t('checkout.cart.itemLabels.name')
                        }}
                    </RouterLink>
                </div>

                <div class="navbar-section">
                    <div class="navbar-section-title">
                        {{ this.$i18n.t('navigation.labels.userPortal') }}
                        <a
                            v-if="isUserLoggedIn"
                            @click="logout"
                            class="navbar-section-title-end"
                        >
                            {{ $t('login.mainActions.logout') }}
                        </a>
                    </div>
                    <RouterLink
                        :to="isUserLoggedIn ? '/account' : '/login'"
                        class="navbar-item navbar-item-hover with-icon"
                    >
                        <SvgIcon
                            icon="icon--web-application_user"
                            class="is-dark"
                            :grow-by-height="true"
                        />

                        {{
                            isUserLoggedIn
                                ? $t('navigation.labels.account')
                                : $t('login.mainActions.login')
                        }}
                    </RouterLink>
                </div>
                <div class="navbar-section">
                    <div class="navbar-section-title">
                        {{ this.$i18n.t('navigation.labels.language') }}
                    </div>
                    <div class="navbar-item">
                        <language-switch />
                    </div>
                </div>
                <business-customer-switch class="navbar-item" />
            </div>
            <div
                class="navbar-end"
                v-if="
                    [
                        viewports.DESKTOP.name,
                        viewports.WIDESCREEN.name,
                        viewports.FULL_HD.name,
                    ].includes(currentViewport)
                "
            >
                <div class="navbar-end">
                    <business-customer-switch class="navbar-item" />
                    <div class="navbar-item">
                        <language-switch />
                    </div>
                    <RouterLink
                        v-for="(navigationLink, index) in topNavigation"
                        :to="navigationLink.link"
                        v-bind:key="'navigation_top_' + index"
                        class="navbar-item"
                    >
                        {{ navigationLink.title }}
                    </RouterLink>
                    <RouterLink
                        v-if="Object.keys(allCartItems).length > 0"
                        to="/cart"
                        class="navbar-item navbar-item-cart with-icon"
                    >
                        <div class="has-icon-after has-text-primary-dark">
                            {{ Object.keys(allCartItems).length }}
                            {{
                                Object.keys(allCartItems).length > 1
                                    ? this.$i18n.t(
                                          'checkout.cart.itemLabels.namePlural'
                                      )
                                    : this.$i18n.t(
                                          'checkout.cart.itemLabels.name'
                                      )
                            }}
                        </div>
                        <SvgIcon
                            icon="icon--web-application_cart"
                            class="is-primary-dark is-size-4"
                        />
                    </RouterLink>
                </div>
            </div>
        </div>
    </nav>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { orderProcess } from '../../store/constants/order-process';
import { viewports } from '../../store/constants/viewports';
import SvgIcon from '../svg/SvgIcon';
import { eventBus } from '../../events/event-bus';
import LanguageSwitch from '../global/LanguageSwitch';
import BusinessCustomerSwitch from '../checkout/user-preferences/BusinessCustomerSwitch';

export default {
    name: 'MainNavigation',
    components: { LanguageSwitch, SvgIcon, BusinessCustomerSwitch },
    data() {
        return {
            menuIsExpanded: false,
            returnToCheckoutAfterClose: false,
            viewports: viewports,
        };
    },
    computed: {
        ...mapGetters([
            'screenSize',
            'currentViewport',
            'isCheckoutOpen',
            'cmsPage',
            'isUserLoggedIn',
            'allCartItems',
        ]),
        ...mapActions(['toggleCheckoutVisibility']),
        mainNavigation() {
            if (this.cmsPage.page) {
                return this.cmsPage.page.navigation.main;
            }
            return null;
        },
        topNavigation() {
            if (this.cmsPage.page) {
                return this.cmsPage.page.navigation.top;
            }
            return null;
        },
        currentOrderProcessStep: {
            get() {
                return this.$store.getters['currentOrderProcessStep'];
            },
            set(orderProcessStep) {
                this.$store.dispatch(
                    'setCurrentOrderProcessStep',
                    orderProcessStep
                );
            },
        },
        showUploadButton() {
            return (
                this.currentOrderProcessStep ===
                    orderProcess.STEPS.NO_ORDER_ONGOING ||
                this.currentOrderProcessStep ===
                    orderProcess.STEPS.ORDER_COMPLETED
            );
        },
    },
    methods: {
        resetOrderProcess() {
            this.currentOrderProcessStep = orderProcess.STEPS.NO_ORDER_ONGOING;
            this.$store.dispatch('clearCurrentCartItem');
            this.$router.push(
                `/${
                    this.$i18n.locale.split('-')[0] !== 'de'
                        ? this.$i18n.locale.split('-')[0]
                        : ''
                }`
            );
        },
        logout() {
            this.$store.dispatch('logoutUser');
            let route = this.$route.path;
            if (
                route === '/account' ||
                route === '/password-update' ||
                route === '/mail-update' ||
                route === '/profile-update' ||
                route.includes('/order-details')
            ) {
                this.$router.push('/');
            }
            eventBus.logout();
        },
        showFileUpload() {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            this.currentOrderProcessStep = orderProcess.STEPS.FILE_UPLOAD;
            this.$store.dispatch('setCheckoutVisibility', true);
        },
        toggleMenu() {
            this.menuIsExpanded = !this.menuIsExpanded;
        },
        fetchCMSData() {
            this.$store.dispatch('fetchCMSPage', { urlSlug: this.$route.path });
        },
    },
    created() {
        this.$store.dispatch('fetchMainNavigation');
        this.$store.dispatch('fetchContentNavigation');
        this.$store.dispatch('fetchFooterNavigation');
        this.$store.dispatch('createCart');
    },
};
</script>

<style></style>
